import React from 'react';
import type { AppraisalForm } from '../../types/appraisal';
import { COMPANY_VALUE_NAMES, type CompanyValueKey } from '../../types/eprFramework';
import { classifyAppraisal } from '../../services/appraisalClassifier';
import AppraisalClassificationCard from './AppraisalClassificationCard';

/**
 * AppraisalSummaryView — read-only render of an AppraisalForm.
 * Used by both the sign-off page and the result-view page.
 */

const AppraisalSummaryView: React.FC<{ appraisal: AppraisalForm }> = ({ appraisal }) => {
  const a = appraisal;
  const eprScore = a.eprSummary?.componentScores?.finalTotal;
  const score360 = a.reviewSummary360?.overallAverage;
  const classification = classifyAppraisal(eprScore, score360);

  return (
    <div className="space-y-6">
      <AppraisalClassificationCard
        classification={classification}
        eprScore={eprScore}
        score360={score360}
      />

      <Section title="EPR Summary">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-stone-500 border-b border-stone-200">
              <th className="text-left py-2 px-1">Component</th>
              <th className="text-right px-1">Weight</th>
              <th className="text-right px-1">Employee Avg</th>
              <th className="text-right px-1">Manager Avg</th>
              <th className="text-right px-1">Final Avg</th>
              <th className="text-right px-1">Weighted</th>
            </tr>
          </thead>
          <tbody>
            {a.eprSummary.componentBreakdown.map(row => (
              <tr key={row.component} className="border-b border-stone-100">
                <td className="py-2 px-1 capitalize">{row.component}</td>
                <td className="text-right px-1">{(row.weight * 100).toFixed(0)}%</td>
                <td className="text-right px-1">{fmt(row.employeeAvg)}</td>
                <td className="text-right px-1">{fmt(row.managerAvg)}</td>
                <td className="text-right px-1 font-medium">{fmt(row.finalAvg)}</td>
                <td className="text-right px-1">{fmt(row.weightedScore)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="py-2 px-1">EPR Total</td>
              <td colSpan={4} />
              <td className="text-right px-1">
                {fmt(a.eprSummary.componentScores.finalTotal)}
                <span className="text-stone-500 font-normal"> / 5.00</span>
              </td>
            </tr>
            {typeof a.eprSummary.componentScores.calibratedTotal === 'number' && (
              <tr className="text-xs text-stone-500">
                <td colSpan={6} className="px-1 italic">
                  Calibrated from raw {fmt(a.eprSummary.componentScores.rawTotal)} → {fmt(a.eprSummary.componentScores.calibratedTotal)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Section>

      <Section title="360 Review Summary">
        <div className="text-sm text-stone-700 space-y-3">
          <div>
            Average company value score:{' '}
            <span className="font-semibold">{fmt(a.reviewSummary360.overallAverage)}</span>
            <span className="text-stone-500"> / 5.00 ({a.reviewSummary360.reviewerCount} reviewer)</span>
          </div>
          {Object.keys(a.reviewSummary360.valueAverages).length > 0 && (
            <table className="w-full text-sm border-collapse">
              <tbody>
                {(Object.keys(a.reviewSummary360.valueAverages) as CompanyValueKey[]).map(k => (
                  <tr key={k} className="border-b border-stone-100">
                    <td className="py-1.5 px-1">{COMPANY_VALUE_NAMES[k]}</td>
                    <td className="text-right px-1 font-medium">{fmt(a.reviewSummary360.valueAverages[k])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <SummaryText label="Strengths"          text={a.reviewSummary360.strengths} />
          <SummaryText label="Things to improve"  text={a.reviewSummary360.thingsToImprove} />
          <SummaryText label="Overall comments"   text={a.reviewSummary360.overallComments} />
        </div>
      </Section>

      {a.managerCombinedNotes && (
        <Section title="Manager Notes">
          <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
            {a.managerCombinedNotes}
          </p>
        </Section>
      )}

      {a.developmentPlan.length > 0 && (
        <Section title="Development Plan">
          <ul className="space-y-3">
            {a.developmentPlan.map(item => (
              <li key={item.id} className="border-l-2 border-stone-300 pl-3">
                <div className="text-sm font-medium text-stone-900">{item.focusArea}</div>
                <div className="text-sm text-stone-700">{item.action}</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  Timeline: {item.timeline}
                  {item.supportNeeded ? ` · Support: ${item.supportNeeded}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Signatures">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <SignatureBox label="Employee" sig={a.employeeSignature} />
          <SignatureBox label="Manager"  sig={a.managerSignature} />
        </div>
        {a.employeeDisagreeComment && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="font-medium mb-1">Catatan ketidaksetujuan karyawan:</div>
            <div className="whitespace-pre-wrap">{a.employeeDisagreeComment}</div>
          </div>
        )}
      </Section>
    </div>
  );
};

function fmt(n: number | undefined | null): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="rounded-xl border border-stone-200 bg-white p-5">
    <h3 className="text-base font-semibold text-stone-900 mb-3">{title}</h3>
    {children}
  </section>
);

const SummaryText: React.FC<{ label: string; text: string }> = ({ label, text }) => {
  if (!text?.trim()) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-stone-500 mt-2">{label}</div>
      <div className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
};

// Firestore Timestamp as it arrives at render time. Narrow inline so the
// SignatureBox stays self-contained.
interface SignatureLike {
  signedByName: string;
  signedAt: { toDate: () => Date } | { seconds: number } | Date | undefined;
  signatureData: string;
}

const SignatureBox: React.FC<{ label: string; sig?: SignatureLike }> = ({ label, sig }) => {
  const isDrawn = sig?.signatureData?.startsWith('data:image/');
  const typedName = sig?.signatureData?.startsWith('typed:')
    ? sig.signatureData.slice(6)
    : undefined;
  const signedAt = sig?.signedAt;
  const signedDate: Date | null =
    !signedAt                           ? null
    : signedAt instanceof Date          ? signedAt
    : 'toDate' in signedAt && typeof signedAt.toDate === 'function' ? signedAt.toDate()
    : 'seconds' in signedAt             ? new Date(signedAt.seconds * 1000)
    : null;
  // English locale per ENGINEERING.md §1 (id-ID was an unintended leak).
  const signedAtStr = signedDate
    ? signedDate.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : undefined;
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      {sig ? (
        <>
          {isDrawn && (
            <div className="mt-1 mb-2 rounded-md bg-white border border-stone-200 p-1">
              <img
                src={sig.signatureData}
                alt={`Signature of ${sig.signedByName}`}
                className="w-full max-h-24 object-contain"
              />
            </div>
          )}
          <div className="text-sm font-medium text-stone-900">{sig.signedByName}</div>
          {typedName && (
            <div className="text-xs italic text-stone-600">/s/ {typedName}</div>
          )}
          {signedAtStr && (
            <div className="text-[10px] text-stone-500 mt-0.5">{signedAtStr}</div>
          )}
        </>
      ) : (
        <div className="text-sm text-stone-500 italic">Not yet signed</div>
      )}
    </div>
  );
};

export default AppraisalSummaryView;
